import React, { useState } from 'react';
import { Card, Row, Col, Table, Select, Button, Statistic, Empty, Tag } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useApp } from '../context/AppContext';
import { formatCurrency, formatNumber, formatConsumption, formatAmount } from '../utils/formatters';

const ReportPage = () => {
  const { invoices, payments, getUnpaidInvoices } = useApp();
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const unpaidInvoices = getUnpaidInvoices();

  // Filter invoices by month/year
  const monthInvoices = invoices.filter(inv => {
    const invDate = new Date(inv.billingDate);
    return invDate.getMonth() === selectedMonth && invDate.getFullYear() === selectedYear;
  });

  const monthPayments = payments.filter(pay => {
    const payDate = new Date(pay.paymentDate);
    return payDate.getMonth() === selectedMonth && payDate.getFullYear() === selectedYear;
  });

  const totalRevenue = monthPayments.reduce((sum, p) => sum + p.amount, 0);
  const totalInvoiced = monthInvoices.reduce((sum, i) => sum + i.totalAmount, 0);
  const totalConsumption = monthInvoices.reduce((sum, i) => sum + i.totalConsumption, 0);

  const unpaidColumns = [
    {
      title: 'Facture',
      dataIndex: 'invoiceNumber',
      key: 'invoiceNumber'
    },
    {
      title: 'Abonné',
      dataIndex: 'subscriberName',
      key: 'subscriberName'
    },
    {
      title: 'Montant impayé',
      dataIndex: 'remainingAmount',
      key: 'remainingAmount',
      render: (amount) => <span style={{ color: 'red', fontWeight: 'bold' }}>{formatCurrency(amount)}</span>
    },
    {
      title: 'Date d\'échéance',
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: (date) => {
        const isOverdue = new Date() > new Date(date);
        return (
          <Tag color={isOverdue ? 'red' : 'orange'}>
            {new Date(date).toLocaleDateString('fr-FR')}
          </Tag>
        );
      }
    }
  ];

  const consumptionColumns = [
    {
      title: 'Abonné',
      dataIndex: 'subscriberName',
      key: 'subscriberName'
    },
    {
      title: 'Consommation (m³)',
      dataIndex: 'totalConsumption',
      key: 'totalConsumption',
      render: (consumption) => formatConsumption(consumption)
    },
    {
      title: 'Montant facturé',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (amount) => formatCurrency(amount)
    }
  ];

  const revenueColumns = [
    {
      title: 'Date',
      dataIndex: 'paymentDate',
      key: 'paymentDate',
      render: (date) => new Date(date).toLocaleDateString('fr-FR')
    },
    {
      title: 'Montant',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => formatCurrency(amount)
    },
    {
      title: 'Méthode',
      dataIndex: 'method',
      key: 'method'
    }
  ];

  const handleExportReport = () => {
    const report = {
      period: `${selectedMonth + 1}/${selectedYear}`,
      totalInvoiced: formatAmount(totalInvoiced),
      totalRevenue: formatAmount(totalRevenue),
      totalConsumption: formatConsumption(totalConsumption),
      unpaidAmount: formatAmount(invoices.reduce((sum, i) => sum + i.remainingAmount, 0)),
      invoiceCount: monthInvoices.length,
      paymentCount: monthPayments.length
    };

    const dataStr = JSON.stringify(report, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    const exportFileDefaultName = `rapport_${selectedMonth + 1}_${selectedYear}.json`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  return (
    <div>
      <h1>Rapports et Statistiques</h1>

      <Card style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <Select
            value={selectedMonth}
            onChange={setSelectedMonth}
            options={Array.from({ length: 12 }, (_, i) => ({
              label: new Date(2024, i).toLocaleString('fr-FR', { month: 'long' }),
              value: i
            }))}
            style={{ width: 150 }}
          />
          <Select
            value={selectedYear}
            onChange={setSelectedYear}
            options={Array.from({ length: 5 }, (_, i) => ({
              label: new Date().getFullYear() - i,
              value: new Date().getFullYear() - i
            }))}
            style={{ width: 100 }}
          />
          <Button type="primary" icon={<DownloadOutlined />} onClick={handleExportReport}>
            Exporter
          </Button>
        </div>
      </Card>

      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total facturé"
              value={totalInvoiced}
              suffix=" FCFA"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Revenus encaissés"
              value={totalRevenue}
              suffix=" FCFA"
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total impayé"
              value={invoices.reduce((sum, i) => sum + i.remainingAmount, 0)}
              suffix=" FCFA"
              valueStyle={{ color: '#f5222d' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Consommation totale"
              value={totalConsumption}
              suffix="m³"
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Factures en attente de paiement">
            {unpaidInvoices.length > 0 ? (
              <Table
                columns={unpaidColumns}
                dataSource={unpaidInvoices}
                rowKey="id"
                pagination={{ pageSize: 5 }}
                size="small"
              />
            ) : (
              <Empty description="Aucune facture impayée" />
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Consommation par abonné">
            {monthInvoices.length > 0 ? (
              <Table
                columns={consumptionColumns}
                dataSource={monthInvoices}
                rowKey="id"
                pagination={false}
                size="small"
              />
            ) : (
              <Empty description="Aucune donnée pour cette période" />
            )}
          </Card>
        </Col>
      </Row>

      <Card title="Paiements reçus" style={{ marginTop: 20 }}>
        {monthPayments.length > 0 ? (
          <Table
            columns={revenueColumns}
            dataSource={monthPayments}
            rowKey="id"
            pagination={false}
            size="small"
          />
        ) : (
          <Empty description="Aucun paiement pour cette période" />
        )}
      </Card>
    </div>
  );
};

export default ReportPage;
